import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import { complaintAPI } from '../utils/api';
import { 
  MessageSquare, Plus, Edit2, Trash2, ToggleLeft, ToggleRight,
  ChevronLeft, X, Check, AlertCircle, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Empty form state
const EMPTY_NATURE = {
  name: '',
  description: '',
  is_active: true,
};

export default function ComplaintNaturesManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [natures, setNatures] = useState([]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingNature, setEditingNature] = useState(null);
  const [formData, setFormData] = useState(EMPTY_NATURE);
  const [saving, setSaving] = useState(false);

  // Fetch complaint natures
  useEffect(() => {
    fetchNatures();
  }, []);

  const fetchNatures = async () => {
    setLoading(true);
    try {
      const response = await complaintAPI.getAllNatures();
      setNatures(response.data || []);
    } catch (error) {
      console.error('Error fetching complaint natures:', error);
      toast.error('Failed to load complaint types');
    } finally {
      setLoading(false);
    }
  };

  // Handle form change
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Open modal for new nature
  const handleAddNew = () => {
    setEditingNature(null);
    setFormData(EMPTY_NATURE);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (nature) => {
    setEditingNature(nature);
    setFormData({
      name: nature.name || '',
      description: nature.description || '',
      is_active: nature.is_active !== false,
    });
    setShowModal(true);
  };

  // Save nature
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingNature) {
        await complaintAPI.updateNature(editingNature.id, formData);
        toast.success('Complaint type updated successfully');
      } else {
        await complaintAPI.createNature(formData);
        toast.success('Complaint type created successfully');
      }
      
      setShowModal(false);
      fetchNatures();
    } catch (error) {
      console.error('Error saving complaint nature:', error);
      toast.error(error.response?.data?.detail || 'Failed to save complaint type');
    } finally {
      setSaving(false);
    }
  };

  // Delete nature
  const handleDelete = async (nature) => {
    if (nature.is_system) {
      toast.error('Cannot delete system complaint types. Disable them instead.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${nature.name}"?`)) return;
    
    try {
      await complaintAPI.deleteNature(nature.id);
      toast.success('Complaint type deleted');
      fetchNatures();
    } catch (error) {
      console.error('Error deleting complaint nature:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete complaint type');
    }
  };

  // Toggle active status
  const handleToggleActive = async (nature) => {
    try {
      await complaintAPI.updateNature(nature.id, {
        name: nature.name,
        description: nature.description || '',
        is_active: !nature.is_active
      });
      toast.success(`Complaint type ${nature.is_active ? 'disabled' : 'enabled'}`);
      fetchNatures();
    } catch (error) {
      console.error('Error toggling complaint nature:', error);
      toast.error('Failed to update complaint type');
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="complaint-natures-management">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-[260px]">
        <TopHeader />
        <main className="flex-1 p-6 overflow-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-800">Complaint Types</h1>
              <p className="text-sm text-slate-500">Manage complaint types for client complaints</p>
            </div>
            <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={handleAddNew}>
              <Plus size={16} />
              Add Complaint Type
            </Button>
          </div>

          {/* Info Card */}
          <Card className="border-0 shadow-sm mb-6 bg-blue-50 border-l-4 border-l-blue-500">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle size={20} className="text-blue-600" />
              <p className="text-sm text-blue-800">
                System complaint types (marked with lock icon) cannot be deleted but can be disabled. 
                Custom complaint types can be fully managed.
              </p>
            </CardContent>
          </Card>

          {/* Complaint Natures List */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              ) : natures.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare size={48} className="mx-auto mb-3 text-slate-300" />
                  <p>No complaint types found</p>
                  <Button className="mt-4" onClick={handleAddNew}>Add First Complaint Type</Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Name</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Description</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Type</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Status</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {natures.map((nature) => (
                        <tr key={nature.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {nature.is_system && <Lock size={14} className="text-slate-400" />}
                              <span className="font-medium text-slate-800">{nature.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">{nature.description || '-'}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              nature.is_system 
                                ? 'bg-slate-100 text-slate-600' 
                                : 'bg-teal-100 text-teal-700'
                            }`}>
                              {nature.is_system ? 'System' : 'Custom'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => handleToggleActive(nature)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                nature.is_active 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                              }`}
                            >
                              {nature.is_active ? (
                                <>
                                  <ToggleRight size={14} />
                                  Active
                                </>
                              ) : (
                                <>
                                  <ToggleLeft size={14} />
                                  Disabled
                                </>
                              )}
                            </button>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleEdit(nature)}
                                title="Edit"
                              >
                                <Edit2 size={16} className="text-slate-500" />
                              </Button>
                              {!nature.is_system && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleDelete(nature)}
                                  title="Delete"
                                >
                                  <Trash2 size={16} className="text-red-500" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Add/Edit Nature Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingNature ? 'Edit Complaint Type' : 'Add Complaint Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Delayed Response"
                data-testid="nature-name"
                disabled={editingNature?.is_system}
              />
              {editingNature?.is_system && (
                <p className="text-xs text-slate-400 mt-1">System complaint types cannot be renamed</p>
              )}
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description of this complaint type..."
                rows={2}
                data-testid="nature-description"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => handleChange('is_active', v)}
                id="active"
              />
              <Label htmlFor="active" className="cursor-pointer">Active (visible to clients)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? 'Saving...' : editingNature ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
